import React from "react";
import {
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaExchangeAlt,
  FaEnvelope,
} from "react-icons/fa";
import { FiHome } from "react-icons/fi";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="w-full font-nunito bg-gradient-to-b from-gray-50 to-white text-gray-800 px-4 py-10 sm:px-6 lg:px-16">
      {/* Back to Home Button */}
      <div className="flex justify-start  mb-6">
        <Link
          to="/"
          className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
        >
          <FiHome className="text-pink-500" />
          <span className="text-purple-700 font-medium">Back to Home</span>
        </Link>
      </div>
      <div className="w-full max-w-8xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-cormorant font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-[#f2a5e8] to-[#9ea3f5]">
            Privacy Policy
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Last Updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        <div className="space-y-10">
          {/* Section 1 */}
          <section className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <FaShieldAlt className="text-[#9ea3f5] text-2xl mr-3" />
              <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#bbbefe] to-[#9ea3f5]">
                Information We Collect
              </h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                We collect information to provide better services to all our
                users. The types of personal data we collect include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Account Information:</strong> Name, email address,
                  date of birth, and contact details when you register.
                </li>
                <li>
                  <strong>Health Data:</strong> Menstrual cycle information,
                  symptoms, and health metrics you choose to track.
                </li>
                <li>
                  <strong>Usage Data:</strong> How you interact with our
                  services, including pages visited and features used.
                </li>
                <li>
                  <strong>Device Information:</strong> IP address, browser type,
                  operating system, and mobile device identifiers.
                </li>
              </ul>
              <p className="text-sm text-gray-500 mt-2">
                Source: General Data Protection Regulation (GDPR) Article 4
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <FaUserShield className="text-[#e8bffb] text-2xl mr-3" />
              <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#e8bffb] to-[#d099e2]">
                How We Use Your Information
              </h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>We process your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide personalized health insights and predictions</li>
                <li>To improve and develop new features for our services</li>
                <li>
                  To communicate important service updates and security notices
                </li>
                <li>
                  To prevent fraud and ensure the security of our platform
                </li>
                <li>For research and analytics to enhance user experience</li>
              </ul>
              <p>
                We will never sell your personal data to third parties. Data is
                only shared with service providers under strict confidentiality
                agreements.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <FaLock className="text-[#ccdcfd] text-2xl mr-3" />
              <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#ccdcfd] to-[#9ea3f5]">
                Data Security & Retention
              </h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>We implement industry-standard security measures including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>End-to-end encryption for sensitive health data</li>
                <li>Regular security audits and penetration testing</li>
                <li>Two-factor authentication for account access</li>
                <li>Data minimization principles</li>
              </ul>
              <p>
                We retain your personal data only for as long as necessary to
                provide our services or as required by law (typically 3 years
                after account deletion).
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <FaExchangeAlt className="text-[#d099e2] text-2xl mr-3" />
              <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#d099e2] to-[#f2a5e8]">
                Your Rights & Choices
              </h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>Under data protection laws, you have rights including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Access:</strong> Request a copy of your personal data
                </li>
                <li>
                  <strong>Rectification:</strong> Correct inaccurate information
                </li>
                <li>
                  <strong>Erasure:</strong> Delete your personal data
                </li>
                <li>
                  <strong>Restriction:</strong> Limit how we use your data
                </li>
                <li>
                  <strong>Portability:</strong> Receive your data in a
                  structured format
                </li>
                <li>
                  <strong>Objection:</strong> Opt-out of certain processing
                </li>
              </ul>
              <p>
                To exercise these rights, please contact our Data Protection
                Officer at dpo@example.com.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <FaEnvelope className="text-[#bbbefe] text-2xl mr-3" />
              <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#bbbefe] to-[#ccdcfd]">
                Contact Information
              </h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                If you have any questions about this Privacy Policy or our data
                practices, please contact us at:
              </p>
              <p className="font-medium">Email: flowheartshelp@gmail.com</p>
              <p>
                We may update this policy periodically. We'll notify you of
                significant changes through our app or via email.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
